/**
 * Income Form Component
 */

import React from 'react';
import { FormData } from '../types';

interface IncomeFormProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: any) => void;
  onNext: () => void;
  onBack: () => void;
}

const IncomeForm: React.FC<IncomeFormProps> = ({ formData, onChange, onNext, onBack }) => {
  const calculateIncomeGap = () => {
    if (!formData.userIncome || !formData.spouseIncome || formData.userIncome === 0) return 0;

    const higherIncome = Math.max(formData.userIncome, formData.spouseIncome);
    const gap = Math.abs(formData.userIncome - formData.spouseIncome);
    return ((gap / higherIncome) * 100).toFixed(1);
  };

  const incomeGap = calculateIncomeGap();

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Income Information
      </h2>

      <p className="text-gray-600 mb-6">
        Please provide annual income information for both you and your spouse.
      </p>

      <div className="space-y-6">
        {/* User Income */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Annual Income <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-3 text-gray-500">$</span>
            <input
              type="number"
              value={formData.userIncome || ''}
              onChange={(e) => onChange('userIncome', Number(e.target.value))}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
          </div>
          <p className="text-gray-500 text-sm mt-1">
            Total annual income before taxes
          </p>
        </div>

        {/* Spouse Income */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Spouse Annual Income <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-3 text-gray-500">$</span>
            <input
              type="number"
              value={formData.spouseIncome || ''}
              onChange={(e) => onChange('spouseIncome', Number(e.target.value))}
              className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
            />
          </div>
          <p className="text-gray-500 text-sm mt-1">
            Total annual income before taxes
          </p>
        </div>

        {/* Income Gap Display */}
        {formData.userIncome > 0 && formData.spouseIncome > 0 && (
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Income Gap Analysis</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Your Income:</span>
                <div className="font-medium">${formData.userIncome.toLocaleString()}</div>
              </div>
              <div>
                <span className="text-gray-600">Spouse Income:</span>
                <div className="font-medium">${formData.spouseIncome.toLocaleString()}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <span className="text-gray-600">Income Gap:</span>
              <div className="font-bold text-lg text-blue-600">{incomeGap}%</div>
              <p className="text-xs text-gray-500 mt-1">
                This gap may affect the court's decision on property division
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!formData.userIncome || !formData.spouseIncome}
          className="bg-blue-600 text-white font-semibold py-3 px-8 rounded-lg hover:bg-blue-700 transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          Next: Children
        </button>
      </div>
    </div>
  );
};

export default IncomeForm;